import { castError } from '@sketch/utils'
import React, { useEffect, useRef, useState } from 'react'
import * as yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { isApolloError } from 'apollo-client'
import { Stripe } from '@stripe/stripe-js'
import { CardNumberElement } from '@stripe/react-stripe-js'

// Components
import {
  FormikScrollToError,
  Button,
  Checkbox,
  Link,
  pluralize,
  useStripe,
} from '@sketch/components'

// Local Components
import { createFormikValidator } from '../../components/StripeField'
import CreditCardForm from '../../components/CreditCardForm'
import BillingDetailsForm, {
  validationSchema,
} from '../../components/BillingDetailsForm'
import Fieldset from '../../components/Fieldset'
import BillSimulation, {
  BillSimulationProps as BaseBillSimulationProps,
} from '../../components/BillSimulation'
import AddEditors from '../../components/AddEditors'
import { useToast } from '@sketch/toasts'

import {
  useUpdatePaymentDetailsMutation,
  useUpdateBillingDetailsMutation,
} from '@sketch/gql-types'

// Styles
import {
  H1,
  Form,
  Layout,
  PaymentFormContainer,
  BillingDetailsFormFormContainer,
  TermsOfServiceSection,
  ManageSeatsSection,
  BillSummarySection,
  WorkspaceLogoContainer,
  StyledLogo,
  WorkspaceName,
  PartnersInvoicePaymentsInfo,
  ReduceSeatsWarning,
  Sticky,
} from './WorkspaceSubscribePaymentDetails.styles'
import {
  H2,
  Text,
} from 'modules/workspace/views/WorkspaceSubscribeView/WorkspaceSubscribeView.styles'

// Utils
import { confirmPendingSetupIntent } from 'modules/workspace/utils'

// Form values
/**
 * Common values between payment methods (invoice and card)
 */
const BILLING_VALUES = {
  name: '',
  email: '',
  address: '',
  city: '',
  country: 'US',
  postalCode: '',
  cardName: '',
  taxId: '',
  state: '',
  tos: false,
}

/**
 * These fields are related with the credit-card
 * data, in order to get formik like validation
 * we need to include them in the "initial values"
 *
 * these values are only placeholders, in the end,
 * none of their values will be used
 */
const CARD_PAYMENT_VALUES = {
  cardNumber: undefined,
  cardCvc: undefined,
  cardExpirationDate: undefined,
}

const STRIPE_VALUES = { ...BILLING_VALUES, ...CARD_PAYMENT_VALUES }
const STRIPE_ERRORS_TO_FIELDS: Record<string, keyof typeof STRIPE_VALUES> = {
  incorrect_cvc: 'cardCvc',
  processing_error: 'cardNumber',
  expired_card: 'cardExpirationDate',
  card_declined: 'cardNumber',
  incorrect_number: 'cardNumber',
  tax_id_invalid: 'taxId',
}

const stripeValidation = createFormikValidator(
  ['cardCvc', 'cardExpirationDate', 'cardNumber'],
  {
    cardNumber: 'Card Number is required',
    cardCvc: 'CVC is required',
    cardExpirationDate: 'Expiry Date is required',
  }
)

// Formik Validation
const INVOICE_VALIDATION_SCHEMA = yup
  .object()
  .shape({ tos: yup.mixed().oneOf([true], 'Terms of Service must be agreed') })
  .concat(validationSchema)
const CARD_VALIDATION_SCHEMA = yup
  .object()
  .shape({
    cardName: yup.string().trim().required('Name on Card is required'),
  })
  .concat(INVOICE_VALIDATION_SCHEMA)

// Types
type InvoiceForm = typeof BILLING_VALUES
type CardForm = typeof STRIPE_VALUES
type FormValues = InvoiceForm | CardForm

interface SubmitInvoiceValues extends InvoiceForm {
  seats?: number
}
type SubmitValues = SubmitInvoiceValues | CardForm

type PaymentMethod = 'invoice' | 'card'

interface BillSimulationProps extends BaseBillSimulationProps {
  onEditorsChange?: (editors: number) => void
}

interface CommonWorkspaceSubscribePaymentDetailsProps {
  title: string
  billSimulation: BillSimulationProps
  hasSCAError?: boolean
  stripe?: Stripe
  customerId?: string
  variant?: 'default' | 'partner'
  paymentMethod?: PaymentMethod
  billingInfo?: Partial<typeof BILLING_VALUES>
  onCompleteSubscription?: () => void
  onSubmit: (values: SubmitValues) => Promise<void>
}

interface Workspace {
  name: string
  avatar?: string | null
}

type WorkspaceSubscribePaymentDetailsDefault = CommonWorkspaceSubscribePaymentDetailsProps & {
  workspace?: never
}

type WorkspaceSubscribePaymentDetailsPartners = CommonWorkspaceSubscribePaymentDetailsProps & {
  workspace: Workspace
}

export type WorkspaceSubscribePaymentDetailsProps =
  | WorkspaceSubscribePaymentDetailsDefault
  | WorkspaceSubscribePaymentDetailsPartners

function isCardPayment(values: FormValues): values is CardForm {
  return (values as CardForm).cardName !== undefined
}

// Helpers
const getInitialFormValues = (
  method: PaymentMethod,
  billingInfo?: Partial<typeof BILLING_VALUES>
): FormValues => {
  if (method === 'card') return STRIPE_VALUES

  return { ...BILLING_VALUES, ...billingInfo }
}

export const WorkspaceSubscribePaymentDetails: React.FC<WorkspaceSubscribePaymentDetailsProps> = ({
  title,
  billSimulation,
  workspace,
  billingInfo,
  hasSCAError,
  stripe,
  customerId,
  variant = 'default',
  paymentMethod = 'card',
  onCompleteSubscription,
  onSubmit,
}) => {
  const formRef = useRef<HTMLFormElement>(null)
  const { showToast } = useToast()
  const initialSeatsNumber = useRef<number>(0)
  const [showReduceSeatsWarning, setShowReduceSeatsWarning] = useState(false)
  const { elements } = useStripe()
  const [
    updatePaymentDetails,
    { loading: loadingUpdateCC },
  ] = useUpdatePaymentDetailsMutation({
    onError: 'show-toast',
  })
  const [updateBillingDetails] = useUpdateBillingDetailsMutation({
    onError: 'show-toast',
  })
  const [loadingCC, setLoadingCC] = useState(false)
  useEffect(() => {
    // Reduce seats warning should show only for partners
    if (variant === 'default') return

    // Since seats number is not available by the time this components render
    // for the first type, we set to 0 and assume any value different than that
    // mean the component already reveived the values from the query.
    if (!initialSeatsNumber.current && billSimulation.numberEditors) {
      initialSeatsNumber.current = billSimulation.numberEditors
      return
    }

    setShowReduceSeatsWarning(
      billSimulation.numberEditors < initialSeatsNumber.current
    )
  }, [variant, billSimulation.numberEditors])

  const numberOfEditorSeatsHelpText = `The Workspace is currently being billed for ${
    billSimulation.numberEditors
  } Editor ${pluralize(
    'Seat',
    'Seats',
    billSimulation.numberEditors
  )}. Once you‘ve accepted your role as a Partner, you can add more Editors from the Billing screen — which will be included in the next billing cycle.`

  const validate = (values: FormValues) =>
    paymentMethod === 'card' ? stripeValidation(values as CardForm) : undefined

  const updateBillingDetailsRetry = async ({
    name,
    email,
    address,
    city,
    postalCode,
    country,
    taxId,
    state,
  }: FormValues) => {
    if (customerId) {
      await updateBillingDetails({
        variables: {
          input: {
            customerId,
            email,
            name,
            taxId,
            address: {
              city,
              country,
              line1: address,
              postalCode,
              state,
            },
          },
        },
      })
    }
  }
  const handleCCRetry = async (cardName: string, correctValues: FormValues) => {
    try {
      if (!stripe || !elements) {
        return
      }
      const cardElement = elements.getElement(CardNumberElement)
      const { error, token } = await stripe.createToken(cardElement!, {
        name: cardName,
      })

      if (error || !token || !customerId) {
        showToast(
          error?.message || "It wasn't possible to use your credit-card",
          'negative'
        )
        return
      }

      const { data, error: mutationError } = await updatePaymentDetails({
        variables: {
          input: {
            customerId,
            paymentSource: token.id,
          },
        },
      })

      const subscription = data?.updatePaymentDetails.customer.subscription

      if (mutationError) return

      // If the card does not support 3DS
      if (subscription && subscription.pendingScaToken) {
        const response = await confirmPendingSetupIntent(
          stripe,
          subscription?.pendingScaToken
        )

        if (response && response.error) {
          showToast(
            'Unable to complete 3D Secure authentication. Try again, or use a different credit card',
            'negative'
          )
          return
        }
      }
      updateBillingDetailsRetry(correctValues)
      onCompleteSubscription && onCompleteSubscription()
    } finally {
      setLoadingCC(false)
    }
  }

  const handleOnSubmit = async (
    values: FormValues,
    actions: FormikHelpers<FormValues>
  ) => {
    try {
      if (isCardPayment(values)) {
        const {
          cardCvc,
          cardExpirationDate,
          cardNumber,
          ...correctValues
        } = values

        if (hasSCAError) {
          setLoadingCC(true)
          handleCCRetry(values.cardName, correctValues)
        } else {
          await onSubmit(correctValues)
        }
      } else {
        await onSubmit({ ...values, seats: billSimulation.numberEditors })
      }
    } catch (e) {
      const error = castError(e)
      if (isApolloError(error)) {
        error.graphQLErrors.forEach(({ extensions, message }) => {
          if (message === 'Invalid value for us_ein.') {
            actions.setFieldError(
              STRIPE_ERRORS_TO_FIELDS['tax_id_invalid'],
              'Please enter a valid VAT or tax ID number.'
            )
            return
          }
          const formField = STRIPE_ERRORS_TO_FIELDS[extensions?.stripeErrorCode]
          formField && actions.setFieldError(formField, message)
        })
      }
    } finally {
      actions.setSubmitting(false)
    }
  }

  return (
    <>
      {workspace && variant === 'partner' && (
        <WorkspaceLogoContainer>
          <StyledLogo
            workspaceName={workspace.name}
            src={workspace.avatar}
            size="24px"
          />
          <WorkspaceName>{workspace.name}</WorkspaceName>
        </WorkspaceLogoContainer>
      )}

      <H1 $variant={variant}>{title}</H1>
      <Formik
        initialValues={getInitialFormValues(paymentMethod, billingInfo)}
        validationSchema={
          paymentMethod === 'card'
            ? CARD_VALIDATION_SCHEMA
            : INVOICE_VALIDATION_SCHEMA
        }
        validate={values => validate(values)}
        onSubmit={handleOnSubmit}
        enableReinitialize
      >
        {({ isSubmitting, touched, errors, handleChange, values }) => (
          <Form ref={formRef}>
            <Layout>
              <div>
                <FormikScrollToError formRef={formRef} />
                <Fieldset>
                  {paymentMethod === 'card' && (
                    <>
                      <H2 size="medium">Payment Method</H2>
                      <Text>Enter your card details</Text>
                      <PaymentFormContainer>
                        <CreditCardForm<any> />
                      </PaymentFormContainer>
                    </>
                  )}

                  <H2 size="medium">
                    {paymentMethod === 'card'
                      ? 'Your Details'
                      : 'Review Invoice Details'}
                  </H2>

                  {variant === 'partner' && (
                    <PartnersInvoicePaymentsInfo type="information">
                      You’re currently using invoices as your preferred payment
                      method. To change your payment method or details, contact
                      <br />
                      <Link external href="mailto:partners@sketch.com">
                        partners@sketch.com
                      </Link>
                    </PartnersInvoicePaymentsInfo>
                  )}

                  <BillingDetailsFormFormContainer>
                    <BillingDetailsForm<FormValues>
                      disabled={paymentMethod === 'invoice'}
                    />
                  </BillingDetailsFormFormContainer>
                </Fieldset>
              </div>

              <div>
                {variant === 'partner' && (
                  <ManageSeatsSection>
                    <H2 size="medium">Manage Editor Seats</H2>
                    <AddEditors
                      description="Schedule Editor Seats for the next billing cycle"
                      helpText={numberOfEditorSeatsHelpText}
                      seats={billSimulation.numberEditors}
                      onChange={editors => {
                        billSimulation.onEditorsChange?.(editors)
                      }}
                    />
                    {showReduceSeatsWarning && (
                      <ReduceSeatsWarning type="warning" showIcon={false}>
                        Reducing Seats will mean that one or more current
                        Editors will no longer be able to edit Sketch documents
                        in the next billing cycle.
                      </ReduceSeatsWarning>
                    )}
                  </ManageSeatsSection>
                )}

                <Sticky>
                  <BillSummarySection $variant={variant}>
                    <H2 size="medium">Bill Summary</H2>
                    <BillSimulation variant={variant} {...billSimulation} />
                  </BillSummarySection>

                  <TermsOfServiceSection>
                    <label htmlFor="terms-of-service" className="sr-only">
                      I’ve read and agree to the Sketch Terms of Service and
                      order details.
                    </label>
                    <Form.Field
                      name="tos"
                      errorText={touched.tos ? errors.tos : undefined}
                    >
                      <Checkbox
                        name="tos"
                        id="terms-of-service"
                        variant="untinted"
                        checked={values.tos}
                        onChange={handleChange}
                      >
                        I’ve read and agree to the Sketch{' '}
                        <Link
                          variant="secondary"
                          isUnderlined
                          external
                          href="https://www.sketch.com/tos"
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          Terms of Service
                        </Link>{' '}
                        and order details.
                      </Checkbox>
                    </Form.Field>
                    <Button
                      variant="primary"
                      fill
                      type="submit"
                      loading={isSubmitting || loadingUpdateCC || loadingCC}
                    >
                      Confirm Payment
                    </Button>
                  </TermsOfServiceSection>
                </Sticky>
              </div>
            </Layout>
          </Form>
        )}
      </Formik>
    </>
  )
}
